import React from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, BarChart3, X, MessageSquare } from "lucide-react";
import { TradingSymbol } from "@/utils/mockData";

interface MobileTradingDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  symbol: TradingSymbol | null;
  onBuyClick: () => void;
  onSellClick: () => void;
  onChartClick: () => void;
  onAskAIClick?: () => void;
}

export const MobileTradingDrawer: React.FC<MobileTradingDrawerProps> = ({
  open,
  onOpenChange,
  symbol,
  onBuyClick,
  onSellClick,
  onChartClick,
  onAskAIClick,
}) => {
  if (!symbol) return null;

  const priceChange = symbol.buyPrice - symbol.close;
  const priceChangePercent = ((priceChange / symbol.close) * 100);
  
  // Format price with symbol's decimal precision
  const formatPrice = (value: number) => {
    const decimalPlaces = symbol.digit || 2;
    return value.toFixed(decimalPlaces);
  };
  
  const getPriceChangeColor = () => {
    if (priceChange > 0) return "text-green-500";
    if (priceChange < 0) return "text-red-500";
    return "text-muted-foreground";
  };

  const getPriceChangeIcon = () => {
    if (priceChange > 0) return <TrendingUp className="h-4 w-4" />;
    if (priceChange < 0) return <TrendingDown className="h-4 w-4" />;
    return null;
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[80vh]">
        <DrawerHeader className="relative">
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4"
            >
              <X className="h-4 w-4" />
            </Button>
          </DrawerClose>
          <DrawerTitle className="text-left text-xl font-bold">
            {symbol.name}
          </DrawerTitle>
        </DrawerHeader>

        <div className="px-6 pb-6 space-y-6">
          {/* Price Information */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  ₹{formatPrice(symbol.buyPrice)}
                </div>
                <div className={`flex items-center gap-1 text-sm ${getPriceChangeColor()}`}>
                  {getPriceChangeIcon()}
                  <span>
                    {priceChange > 0 ? "+" : ""}{formatPrice(priceChange)} ({priceChangePercent > 0 ? "+" : ""}{priceChangePercent.toFixed(2)}%)
                  </span>
                </div>
              </div>
            </div>

            {/* Bid/Ask Prices */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
              <div className="text-center">
                <div className="text-xs text-muted-foreground mb-1">BID</div>
                <div className="font-semibold text-blue-600">
                  ₹{formatPrice(symbol.buyPrice)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-muted-foreground mb-1">ASK</div>
                <div className="font-semibold text-orange-600">
                  ₹{formatPrice(symbol.sellPrice)}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Buy/Sell Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={onBuyClick}
                className="h-12 bg-green-600 hover:bg-green-700 text-white font-semibold"
              >
                BUY
              </Button>
              <Button
                onClick={onSellClick}
                className="h-12 bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                SELL
              </Button>
            </div>

            {/* Chart Button */}
            <Button
              onClick={onChartClick}
              variant="outline"
              className="w-full h-12 font-semibold"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              VIEW CHART
            </Button>

            {/* Ask AI Button */}
            {onAskAIClick && (
              <Button
                onClick={onAskAIClick}
                variant="outline"
                className="w-full h-12 font-semibold"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                ASK AI
              </Button>
            )}
          </div>

          {/* Additional Symbol Info */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Open:</span>
              <span>₹{formatPrice(symbol.open)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">High:</span>
              <span>₹{formatPrice(symbol.high)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Low:</span>
              <span>₹{formatPrice(symbol.low)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Prev Close:</span>
              <span>₹{formatPrice(symbol.close)}</span>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};